import { useMemo, useState } from 'react';
import { LockClosedIcon, LockOpenIcon } from '@heroicons/react/24/outline';
import {
  useProjectMonthLocks,
  useLockProjectMonth,
  useUnlockProjectMonth,
} from '../../hooks/useProjectMonthLocks';
import { Modal } from '../common/Modal';
import { toErrorMessage } from '../../utils/toErrorMessage';

/**
 * Phase 113 (MKS-208, D-13) — the padlock, on the project detail page where
 * the PM already works.
 *
 * D-04: v1 is manual. A human closes a month after they have reviewed it;
 * nothing locks on a schedule or when an invoice is issued.
 *
 * Locking a month closes it to direct time entry for EVERYONE, admins
 * included (D-02). Hours that still need to land there arrive only through a
 * backdated-hours request, which this panel's sibling handles.
 */

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function monthLabel(year: number, month: number): string {
  return `${MONTH_NAMES[month - 1] ?? month} ${year}`;
}

/** The last `count` whole months, newest first — the ones a PM actually closes. */
function recentMonths(count: number): { year: number; month: number }[] {
  const out: { year: number; month: number }[] = [];
  const now = new Date();
  // Start at last month: the current one is still accruing hours.
  const cursor = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  for (let i = 0; i < count; i += 1) {
    out.push({ year: cursor.getUTCFullYear(), month: cursor.getUTCMonth() + 1 });
    cursor.setUTCMonth(cursor.getUTCMonth() - 1);
  }
  return out;
}

interface ProjectMonthLocksPanelProps {
  projectId: number;
  /** Admin or this project's PM — only they may lock and unlock. */
  canManage: boolean;
}

export function ProjectMonthLocksPanel({ projectId, canManage }: ProjectMonthLocksPanelProps) {
  const { data: locks, isLoading, error } = useProjectMonthLocks(projectId);
  const lockMutation = useLockProjectMonth(projectId);
  const unlockMutation = useUnlockProjectMonth(projectId);

  const [confirming, setConfirming] = useState<{ year: number; month: number } | null>(null);
  const [note, setNote] = useState('');
  const [unlocking, setUnlocking] = useState<{ year: number; month: number } | null>(null);

  const lockByMonth = useMemo(() => {
    const map = new Map<string, NonNullable<typeof locks>[number]>();
    for (const lock of locks ?? []) map.set(`${lock.year}-${lock.month}`, lock);
    return map;
  }, [locks]);

  // The panel is a PM control surface, not a status dashboard: a transient
  // 403/500 must never break the project page.
  if (isLoading || error) return null;

  // The twelve months a PM might still be closing, plus any older month that
  // is already locked, so an existing lock is never invisible here.
  const months = (() => {
    const seen = new Set<string>();
    const out: { year: number; month: number }[] = [];
    for (const m of recentMonths(12)) {
      seen.add(`${m.year}-${m.month}`);
      out.push(m);
    }
    for (const lock of locks ?? []) {
      const key = `${lock.year}-${lock.month}`;
      if (!seen.has(key)) {
        seen.add(key);
        out.push({ year: lock.year, month: lock.month });
      }
    }
    return out.sort((a, b) => b.year - a.year || b.month - a.month);
  })();

  const handleLock = async () => {
    if (!confirming) return;
    try {
      await lockMutation.mutateAsync({
        year: confirming.year,
        month: confirming.month,
        note: note.trim() || undefined,
      });
      setConfirming(null);
      setNote('');
    } catch {
      // Surfaced in the modal.
    }
  };

  const handleUnlock = async () => {
    if (!unlocking) return;
    try {
      await unlockMutation.mutateAsync(unlocking);
      setUnlocking(null);
    } catch {
      // Surfaced in the modal.
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-semibold text-gray-900">Month Locks</h2>
        <span className="text-sm text-gray-500">
          {lockByMonth.size} locked month{lockByMonth.size !== 1 ? 's' : ''}
        </span>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        A locked month is closed to time entry for everyone, admins included. Hours that still
        need to land there arrive only through an approved backdated-hours request.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {months.map(({ year, month }) => {
          const lock = lockByMonth.get(`${year}-${month}`);
          const locked = !!lock;
          return (
            <button
              key={`${year}-${month}`}
              type="button"
              disabled={!canManage}
              onClick={() =>
                locked ? setUnlocking({ year, month }) : setConfirming({ year, month })
              }
              title={
                locked
                  ? `Locked by ${lock.lockedBy?.name ?? 'unknown'}${lock.note ? ` — ${lock.note}` : ''}`
                  : canManage
                    ? `Lock ${monthLabel(year, month)}`
                    : 'Only an admin or this project’s PM can lock a month'
              }
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                locked
                  ? 'border-amber-300 bg-amber-50 text-amber-800'
                  : 'border-gray-200 bg-white text-gray-600'
              } ${canManage ? 'hover:border-gray-400 cursor-pointer' : 'cursor-default opacity-80'}`}
            >
              {locked ? (
                <LockClosedIcon className="w-4 h-4 flex-shrink-0" />
              ) : (
                <LockOpenIcon className="w-4 h-4 flex-shrink-0 text-gray-400" />
              )}
              <span className="truncate">{monthLabel(year, month)}</span>
            </button>
          );
        })}
      </div>

      <Modal
        isOpen={!!confirming}
        onClose={() => setConfirming(null)}
        title={confirming ? `Lock ${monthLabel(confirming.year, confirming.month)}` : ''}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Nobody — including admins — will be able to create, edit or delete an entry on this
            project in{' '}
            <span className="font-medium">
              {confirming ? monthLabel(confirming.year, confirming.month) : ''}
            </span>
            . Hours that still need to land there will have to come through a backdated-hours
            request you approve. You can unlock the month again at any time.
          </p>

          <div>
            <label
              htmlFor="month-lock-note"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Note (optional)
            </label>
            <input
              id="month-lock-note"
              type="text"
              value={note}
              maxLength={500}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Reviewed and invoiced"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {lockMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {toErrorMessage(lockMutation.error, 'Failed to lock the month')}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setConfirming(null)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleLock}
              disabled={lockMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
            >
              {lockMutation.isPending ? 'Locking…' : 'Lock month'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!unlocking}
        onClose={() => setUnlocking(null)}
        title={unlocking ? `Unlock ${monthLabel(unlocking.year, unlocking.month)}` : ''}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Time entry re-opens for everyone assigned to this project. Pending backdated-hours
            requests for this month are left alone — they stay pending and remain approvable.
          </p>

          {unlockMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {toErrorMessage(unlockMutation.error, 'Failed to unlock the month')}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setUnlocking(null)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUnlock}
              disabled={unlockMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {unlockMutation.isPending ? 'Unlocking…' : 'Unlock month'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default ProjectMonthLocksPanel;
